// D5 + D2 — Alerts hub (3 tabs)
//   - 异常 (anomalies)       : auto-detected anomalies (hourly cron, Z-score / burst / silent)
//   - 通知 (notifications)   : alert rule fires; in-app inbox + 已读标记
//   - 规则 (rules)           : alert rule CRUD; webhook URL + channels

import React, { useEffect, useState } from 'react';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import {
  Section,
  PillGroup,
  FilterBar,
  EmptyTable,
  LoadingHint,
  DataTable,
  fmtRelDate,
  fmtPct,
  fmtNum,
  fmtUsd,
} from './analytics-shared.jsx';

const KIND_LABEL = {
  ERROR_SPIKE: '错误率突增',
  LATENCY_SPIKE: '延迟突增',
  TRAFFIC_DROP: '流量下跌',
  CALLER_BURST: '调用方激增',
  ENDPOINT_SILENT: '端点静默',
};

const SEVERITY_COLOR = {
  HIGH: '#c0392b',
  MEDIUM: '#e08e0b',
  LOW: 'var(--fg-3)',
};

const METRIC_LABEL = {
  ERROR_RATE: '错误率',
  P95_LATENCY: 'P95 延迟',
  CALLS_PER_MINUTE: '每分钟调用',
  PENDING_EARNINGS: '待结算金额',
};

const TABS = [
  { v: 'anomalies', l: '异常' },
  { v: 'notifications', l: '通知' },
  { v: 'rules', l: '规则' },
];

export const AnomaliesSection = ({ api }) => {
  const [tab, setTab] = useState('anomalies');
  // 顶部「告警」红点 = 未解决异常 + 未读通知。默认 tab 是「异常」,但那 3 个未读
  // 往往在「通知」里 —— 点进来落在空的「异常」tab 就显得"什么都没有"。这里取两边
  // 数量:在 tab 上显示计数,并把默认 tab 切到真正有内容的那个。
  const [counts, setCounts] = useState({ anomalies: 0, notifications: 0 });
  const [defaulted, setDefaulted] = useState(false);

  useEffect(() => {
    if (!api?.id) return;
    let cancelled = false;
    Promise.all([
      XApi.providerDashboardService.anomalies({ resolved: 'open', apiId: api.id, limit: 1 }).catch(() => null),
      XApi.providerDashboardService.listAlertNotifications({ unreadOnly: true, limit: 1 }).catch(() => null),
    ]).then(([anom, notifs]) => {
      if (cancelled) return;
      const a = anom?.unreadCount ?? anom?.items?.length ?? 0;
      const n = notifs?.unreadCount ?? 0;
      setCounts({ anomalies: a, notifications: n });
      // 只在首次定位一次,之后尊重用户的手动切换
      if (!defaulted) {
        if (a === 0 && n > 0) setTab('notifications');
        setDefaulted(true);
      }
    });
    return () => { cancelled = true; };
  }, [api?.id]);

  const tabsWithCounts = TABS.map((t) => ({
    ...t,
    l: counts[t.v] > 0 ? `${t.l} (${counts[t.v]})` : t.l,
  }));

  return (
    <div style={{ padding: '24px 32px 64px' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
          告警
        </h2>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
          自动异常检测 · 规则触发通知 · webhook + 站内 inbox
        </div>
      </div>

      <FilterBar>
        <PillGroup value={tab} options={tabsWithCounts} onChange={setTab} />
      </FilterBar>

      {tab === 'anomalies' && <AnomaliesTab api={api} />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'rules' && <RulesTab api={api} />}
    </div>
  );
};

// ── Anomalies tab (D5) ──────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { v: 'open', l: '未解决' },
  { v: 'resolved', l: '已解决' },
  { v: 'all', l: '全部' },
];

const AnomaliesTab = ({ api }) => {
  const { navigate } = XShell.useRoute();
  const apiId = api?.id;
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  const load = () => {
    setLoading(true);
    XApi.providerDashboardService
      .anomalies({ resolved: filter, apiId, limit: 100 })
      .then(setData)
      .finally(() => setLoading(false));
  };
  useEffect(load, [apiId, filter]);

  const handleResolve = async (id) => {
    setResolving(id);
    try {
      await XApi.providerDashboardService.resolveAnomaly(id, '');
      load();
    } finally { setResolving(null); }
  };

  return (
    <Section
      title={`异常 (${data?.items?.length || 0})`}
    >
      <FilterBar>
        <PillGroup label="状态" value={filter} options={STATUS_OPTIONS} onChange={setFilter} />
      </FilterBar>
      {loading && !data ? (
        <LoadingHint />
      ) : !data?.items?.length ? (
        filter === 'open'
          ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>🎉 没有未解决的异常 — 一切正常</div>
          : <EmptyTable cols={5} rows={3} hint="暂无异常" />
      ) : (
        <DataTable>
          <thead>
            <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
              <th style={{ padding: '6px 8px', width: 60 }}>严重度</th>
              <th style={{ padding: '6px 8px' }}>类型</th>
              <th style={{ padding: '6px 8px' }}>描述</th>
              <th style={{ padding: '6px 8px' }}>检测于</th>
              <th style={{ padding: '6px 8px', width: 220 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((a) => (
              <tr key={a.id} style={{ borderTop: '1px solid var(--bd-1)' }}>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{
                    padding: '1px 8px', background: SEVERITY_COLOR[a.severity] + '22',
                    color: SEVERITY_COLOR[a.severity], fontSize: 10, fontWeight: 500, borderRadius: 4,
                  }}>{a.severity}</span>
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--fg-1)' }}>{KIND_LABEL[a.kind] || a.kind}</td>
                <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--fg-2)', maxWidth: 480, wordBreak: 'break-word' }}>
                  {a.notes || `observed=${a.observedValue} baseline=${a.baselineValue}${a.deviationStd != null ? ' Z=' + a.deviationStd.toFixed(2) : ''}`}
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>{fmtRelDate(a.detectedAt)}</td>
                <td style={{ padding: '6px 8px' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    {a.endpointId && (
                      <button type="button" onClick={() => navigate('workspace-endpoint', { apiId, endpointId: a.endpointId })}
                        style={{ padding: '2px 6px', background: 'transparent', border: '1px solid var(--bd-1)', borderRadius: 3, color: 'var(--fg-2)', fontSize: 10, cursor: 'pointer' }}>
                        看 endpoint →
                      </button>
                    )}
                    {a.callerRef && (
                      <button type="button" onClick={() => navigate('workspace-caller', { apiId, callerId: a.callerRef })}
                        style={{ padding: '2px 6px', background: 'transparent', border: '1px solid var(--bd-1)', borderRadius: 3, color: 'var(--fg-2)', fontSize: 10, cursor: 'pointer' }}>
                        看 caller →
                      </button>
                    )}
                    {!a.resolvedAt && (
                      <button type="button" onClick={() => handleResolve(a.id)} disabled={resolving === a.id}
                        style={{ padding: '2px 8px', background: 'var(--fg-0)', color: 'var(--bg-0)', border: 'none', borderRadius: 3, fontSize: 10, cursor: resolving === a.id ? 'wait' : 'pointer' }}>
                        {resolving === a.id ? '…' : '已处理 ✓'}
                      </button>
                    )}
                    {a.resolvedAt && (
                      <span style={{ fontSize: 10, color: 'var(--fg-3)' }} className="mono">{fmtRelDate(a.resolvedAt)}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </Section>
  );
};

// ── Notifications tab (D2) ──────────────────────────────────────────────
const NotificationsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    XApi.providerDashboardService.listAlertNotifications({ limit: 100 })
      .then((d) => !cancelled && setData(d))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const markAllRead = async () => {
    const unread = (data?.items || []).filter((n) => !n.inappReadAt).map((n) => n.id);
    if (unread.length === 0) return;
    await XApi.providerDashboardService.markAlertRead(unread);
    XApi.providerDashboardService.listAlertNotifications({ limit: 100 }).then(setData);
  };

  return (
    <Section
      title={`通知 (${data?.items?.length || 0}${data?.unreadCount ? ` · ${data.unreadCount} unread` : ''})`}
      action={
        data?.unreadCount > 0 ? (
          <button type="button" onClick={markAllRead} style={{
            padding: '4px 10px', background: 'transparent', border: '1px solid var(--bd-1)',
            borderRadius: 4, color: 'var(--fg-2)', fontSize: 10, cursor: 'pointer',
          }}>全部标记已读</button>
        ) : null
      }
    >
      {loading && !data ? (
        <LoadingHint />
      ) : !data?.items?.length ? (
        <EmptyTable cols={4} rows={3} hint="还没有规则触发的通知" subHint="到「规则」tab 配置触发条件" />
      ) : (
        <DataTable>
          <thead>
            <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
              <th style={{ padding: '6px 8px' }}>规则</th>
              <th style={{ padding: '6px 8px' }}>指标</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>命中值</th>
              <th style={{ padding: '6px 8px' }}>派发</th>
              <th style={{ padding: '6px 8px' }}>触发于</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((n) => {
              const isUnread = !n.inappReadAt;
              return (
                <tr key={n.id} style={{ borderTop: '1px solid var(--bd-1)', background: isUnread ? 'rgba(91,141,239,0.06)' : 'transparent' }}>
                  <td style={{ padding: '6px 8px' }}>
                    {isUnread && <span style={{ color: '#5b8def', marginRight: 6 }}>●</span>}
                    <span style={{ color: 'var(--fg-1)' }}>{n.rule?.name || '(deleted rule)'}</span>
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--fg-2)' }}>
                    {METRIC_LABEL[n.rule?.metric] || n.rule?.metric} {n.rule?.comparator === 'GT' ? '>' : '<'} {n.rule?.threshold}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{n.metricValue.toFixed(3)}</td>
                  <td style={{ padding: '6px 8px', fontSize: 10, color: 'var(--fg-3)' }} className="mono">
                    {(n.channelsSent || []).map((c) => {
                      const s = (n.deliveryStatus || {})[c];
                      const ok = s?.status === 'sent';
                      return <span key={c} style={{ marginRight: 6, color: ok ? '#22a06b' : '#c0392b' }}>{c}{ok ? ' ✓' : ' ×'}</span>;
                    })}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>{fmtRelDate(n.firedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      )}
    </Section>
  );
};

// ── Rules tab (D2 CRUD) ─────────────────────────────────────────────────
const METRIC_OPTIONS = [
  { v: 'ERROR_RATE', l: '错误率（0..1）' },
  { v: 'P95_LATENCY', l: 'P95 延迟（ms）' },
  { v: 'CALLS_PER_MINUTE', l: '每分钟调用' },
  { v: 'PENDING_EARNINGS', l: '待结算 USD' },
];

const RulesTab = ({ api }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = no form open; {} = new rule; {id, ...} = edit existing

  const load = () => {
    setLoading(true);
    XApi.providerDashboardService.listAlertRules().then((r) => {
      setRules(r);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const handleSave = async (form) => {
    const channels = [];
    if (form.inappEnabled) channels.push({ type: 'inapp' });
    if (form.webhookEnabled && form.webhookUrl) {
      channels.push({ type: 'webhook', url: form.webhookUrl, secret: form.webhookSecret || undefined });
    }
    if (channels.length === 0) {
      alert('至少需要一个通知渠道');
      return;
    }
    if (form.id) {
      await XApi.providerDashboardService.updateAlertRule(form.id, {
        name: form.name,
        threshold: parseFloat(form.threshold),
        windowMinutes: parseInt(form.windowMinutes, 10),
        cooldownMinutes: parseInt(form.cooldownMinutes, 10),
        channels,
        enabled: form.enabled !== false,
      });
    } else {
      await XApi.providerDashboardService.createAlertRule({
        name: form.name,
        metric: form.metric,
        comparator: form.comparator,
        threshold: parseFloat(form.threshold),
        windowMinutes: parseInt(form.windowMinutes, 10),
        cooldownMinutes: parseInt(form.cooldownMinutes, 10),
        channels,
        apiServiceId: form.scope === 'api' ? api?.id : undefined,
      });
    }
    setEditing(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!confirm('确认删除？')) return;
    await XApi.providerDashboardService.deleteAlertRule(id);
    load();
  };

  const handleToggle = async (rule) => {
    await XApi.providerDashboardService.updateAlertRule(rule.id, { enabled: !rule.enabled });
    load();
  };

  return (
    <Section
      title={`规则 (${rules.length})`}
      action={
        <button type="button" onClick={() => setEditing({ scope: 'api', metric: 'ERROR_RATE', comparator: 'GT', threshold: 0.05, windowMinutes: 60, cooldownMinutes: 60, inappEnabled: true })}
          style={{ padding: '4px 12px', background: 'var(--fg-0)', color: 'var(--bg-0)', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          + 新建规则
        </button>
      }
    >
      {loading ? (
        <LoadingHint />
      ) : rules.length === 0 ? (
        <EmptyTable cols={5} rows={2} hint="还没有告警规则" subHint="点击右上「+ 新建规则」开始" />
      ) : (
        <DataTable>
          <thead>
            <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
              <th style={{ padding: '6px 8px' }}>规则名</th>
              <th style={{ padding: '6px 8px' }}>条件</th>
              <th style={{ padding: '6px 8px' }}>渠道</th>
              <th style={{ padding: '6px 8px' }}>状态</th>
              <th style={{ padding: '6px 8px' }}>最近触发</th>
              <th style={{ padding: '6px 8px', width: 180 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--bd-1)' }}>
                <td style={{ padding: '6px 8px' }}>
                  <div style={{ color: 'var(--fg-0)' }}>{r.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                    {r.apiServiceId ? `scope: this API` : 'scope: all APIs'}
                  </div>
                </td>
                <td style={{ padding: '6px 8px', fontSize: 11 }}>
                  {METRIC_LABEL[r.metric]} {r.comparator === 'GT' ? '>' : '<'} <span className="mono">{r.threshold}</span>
                  <span style={{ color: 'var(--fg-3)', marginLeft: 6 }}>window {r.windowMinutes}m · cd {r.cooldownMinutes}m</span>
                </td>
                <td style={{ padding: '6px 8px', fontSize: 10, color: 'var(--fg-3)' }} className="mono">
                  {(r.channels || []).map((c) => c.type).join(' · ')}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  {r.autoDisabledAt ? (
                    <span title={`Auto-disabled after ${r.consecutiveFails} webhook fails`}
                      style={{ padding: '1px 6px', background: '#c0392b22', color: '#c0392b', fontSize: 10, borderRadius: 3 }}>
                      AUTO-DISABLED
                    </span>
                  ) : r.enabled ? (
                    <span style={{ padding: '1px 6px', background: '#22a06b22', color: '#22a06b', fontSize: 10, borderRadius: 3 }}>ON</span>
                  ) : (
                    <span style={{ padding: '1px 6px', background: 'var(--bg-1)', color: 'var(--fg-3)', fontSize: 10, borderRadius: 3 }}>OFF</span>
                  )}
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>{r.lastFiredAt ? fmtRelDate(r.lastFiredAt) : '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <button type="button" onClick={() => handleToggle(r)}
                      style={{ padding: '2px 8px', background: 'transparent', border: '1px solid var(--bd-1)', borderRadius: 3, color: 'var(--fg-2)', fontSize: 10, cursor: 'pointer' }}>
                      {r.enabled ? 'disable' : 'enable'}
                    </button>
                    <button type="button" onClick={() => setEditing({
                      id: r.id, name: r.name, metric: r.metric, comparator: r.comparator,
                      threshold: r.threshold, windowMinutes: r.windowMinutes, cooldownMinutes: r.cooldownMinutes,
                      enabled: r.enabled, scope: r.apiServiceId ? 'api' : 'all',
                      inappEnabled: (r.channels || []).some((c) => c.type === 'inapp'),
                      webhookEnabled: (r.channels || []).some((c) => c.type === 'webhook'),
                      webhookUrl: (r.channels || []).find((c) => c.type === 'webhook')?.url || '',
                      webhookSecret: '',
                    })}
                      style={{ padding: '2px 8px', background: 'transparent', border: '1px solid var(--bd-1)', borderRadius: 3, color: 'var(--fg-2)', fontSize: 10, cursor: 'pointer' }}>
                      edit
                    </button>
                    <button type="button" onClick={() => handleDelete(r.id)}
                      style={{ padding: '2px 8px', background: 'transparent', border: '1px solid #c0392b', borderRadius: 3, color: '#c0392b', fontSize: 10, cursor: 'pointer' }}>
                      delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {editing && <RuleFormDialog initial={editing} onSave={handleSave} onClose={() => setEditing(null)} />}
    </Section>
  );
};

// ── Rule create / edit form (modal) ─────────────────────────────────────
const RuleFormDialog = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial);
  const isEdit = !!initial.id;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 30,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(520px, 92vw)', background: 'var(--bg-0)', border: '1px solid var(--bd-1)',
        borderRadius: 8, padding: 24,
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 14 }}>{isEdit ? '编辑规则' : '新建规则'}</h3>

        <Field label="规则名">
          <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="如：Reddit 错误率告警" />
        </Field>

        {!isEdit && (
          <Field label="作用范围">
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} style={inputStyle}>
              <option value="api">仅本 API</option>
              <option value="all">所有 API</option>
            </select>
          </Field>
        )}

        {!isEdit && (
          <Field label="指标">
            <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} style={inputStyle}>
              {METRIC_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        )}

        <Field label="比较">
          <select value={form.comparator} onChange={(e) => setForm({ ...form, comparator: e.target.value })} style={inputStyle} disabled={isEdit}>
            <option value="GT">大于 (&gt;)</option>
            <option value="LT">小于 (&lt;)</option>
          </select>
        </Field>

        <Field label="阈值">
          <input type="number" step="0.001" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} style={inputStyle} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="窗口（分钟）">
            <input type="number" value={form.windowMinutes} onChange={(e) => setForm({ ...form, windowMinutes: e.target.value })} style={inputStyle} />
          </Field>
          <Field label="冷却（分钟）">
            <input type="number" value={form.cooldownMinutes} onChange={(e) => setForm({ ...form, cooldownMinutes: e.target.value })} style={inputStyle} />
          </Field>
        </div>

        <div style={{ marginTop: 14, padding: 12, background: 'var(--bg-1)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 8 }}>通知渠道</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8 }}>
            <input type="checkbox" checked={form.inappEnabled || false} onChange={(e) => setForm({ ...form, inappEnabled: e.target.checked })} />
            站内通知（顶栏 inbox）
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8 }}>
            <input type="checkbox" checked={form.webhookEnabled || false} onChange={(e) => setForm({ ...form, webhookEnabled: e.target.checked })} />
            Webhook (POST 你的 URL，HMAC-SHA256 签名)
          </label>
          {form.webhookEnabled && (
            <div style={{ paddingLeft: 22 }}>
              <input value={form.webhookUrl || ''} onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })} placeholder="https://your-server.example/xapi-alerts" style={{ ...inputStyle, marginBottom: 6 }} />
              <input value={form.webhookSecret || ''} onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })} placeholder="HMAC secret (可选，留空 = 无签名)" style={inputStyle} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--bd-1)', borderRadius: 4, color: 'var(--fg-2)', fontSize: 12, cursor: 'pointer' }}>
            取消
          </button>
          <button type="button" onClick={() => onSave(form)}
            disabled={!form.name}
            style={{ padding: '6px 14px', background: form.name ? 'var(--fg-0)' : 'var(--bg-1)', color: form.name ? 'var(--bg-0)' : 'var(--fg-3)', border: 'none', borderRadius: 4, fontSize: 12, cursor: form.name ? 'pointer' : 'not-allowed' }}>
            {isEdit ? '保存' : '创建'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '6px 8px', background: 'var(--bg-1)',
  border: '1px solid var(--bd-2)', borderRadius: 4, color: 'var(--fg-0)',
  fontSize: 12, boxSizing: 'border-box',
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);
